import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Briefcase, Mail, Rocket, Sparkles, Square, Check, Pencil, ArrowLeft, ArrowRight, Globe, type LucideIcon,
} from 'lucide-react';
import { Field, Input } from '../ui/Field';
import Modal from '../ui/Modal';
import { api } from '../../lib/api';
import { PAGE_START_OPTIONS, type PageStartOption } from '../../lib/pageTemplates';
import type { Page } from '../../lib/types';

interface Props {
  title?: string;
  /** Sitio multi-idioma (según el plan). Si es false, se oculta todo lo de idiomas. */
  multilingual?: boolean;
  onSave: (page: Page) => void;
  onCancel: () => void;
  defaults?: { locale?: string; pageKey?: string; siblingTitle?: string };
}

const ICONS: Record<string, LucideIcon> = {
  users: Users, briefcase: Briefcase, mail: Mail, rocket: Rocket, sparkles: Sparkles, square: Square,
};

type Question = { question: string; options: string[] };
type Phase = 'form' | 'questions' | 'building';

// Animación de revelado/colapso de cada paso (estilo formulario mobile).
const reveal = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto' as const, opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.26, ease: 'easeOut' as const },
};

export default function PageCreateForm({ title = 'Nueva página', multilingual = false, onSave, onCancel, defaults }: Props) {
  const [form, setForm] = useState({
    title: '',
    slug: '',
    locale: defaults?.locale ?? 'es',
    pageKey: defaults?.pageKey ?? '',
  });
  const [option, setOption] = useState<PageStartOption | null>(null);
  const [description, setDescription] = useState('');

  // Acordeón: paso activo (0 identidad · 1 cómo empezar · 2 detalle) + máximo alcanzado.
  const [activeStep, setActiveStep] = useState(0);
  const [maxStep, setMaxStep] = useState(0);

  const [phase, setPhase] = useState<Phase>('form');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Min-height dinámico: el modal crece hasta el paso más amplio y nunca encoge,
  // para evitar el "salto" de tamaño al revelar/comprimir pasos. Se mide la altura
  // natural del contenido (ref interno, sin min-height) y se guarda el máximo.
  const measureRef = useRef<HTMLDivElement>(null);
  const [minH, setMinH] = useState(0);
  useEffect(() => {
    const el = measureRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const h = el.scrollHeight;
      setMinH((prev) => (h > prev ? h : prev));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  function set(key: keyof typeof form, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function slugify(text: string) {
    return '/' + text
      .toString().toLowerCase().trim().normalize('NFD')
      .replace(/[̀-ͯ]/g, '').replace(/\s+/g, '-').replace(/[^a-z0-9-]+/g, '')
      .replace(/--+/g, '-').replace(/^-|-$/g, '');
  }

  function handleTitleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const title = e.target.value;
    setForm((f) => ({ ...f, title, slug: f.slug === '' || f.slug === slugify(f.title) ? slugify(title) : f.slug }));
  }

  const identityValid = !!form.title.trim() && !!form.slug.trim();
  const step0Done = maxStep >= 1;
  const step1Done = !!option && maxStep >= 2;

  // En un sitio bilingüe, crear una página NUEVA genera ambas versiones (ES + EN)
  // enlazadas. Cuando se rellena una versión que falta desde una tarjeta
  // (defaults.locale presente), se crea solo ese idioma.
  const autoBilingual = multilingual && !defaults?.locale;

  function goStep(i: number) { setActiveStep(i); setMaxStep((m) => Math.max(m, i)); }
  function confirmIdentity() { if (identityValid) goStep(1); }
  function chooseOption(opt: PageStartOption) { setOption(opt); goStep(2); }

  function autoPageKey() {
    const fromSlug = form.slug.replace(/^\/+/, '').replace(/\//g, '-');
    return fromSlug || slugify(form.title).replace(/^\/+/, '');
  }
  function enSlug() {
    const base = form.slug.startsWith('/') ? form.slug : `/${form.slug}`;
    return `/en${base}`;
  }

  async function save(id: string, contentJson?: string) {
    if (contentJson && contentJson !== '[]') {
      try { await api.pages.saveContent(id, contentJson); } catch { /* abre vacía si falla */ }
    }
  }

  // Crea la página y, si hay contenido generado, lo guarda antes de abrir el editor.
  // En modo bilingüe crea las dos versiones (ES + EN) con la misma clave.
  async function createWith(contentJson?: string, contentJsonEn?: string) {
    if (autoBilingual) {
      const pageKey = autoPageKey();
      const es = await api.pages.create({ title: form.title, slug: form.slug, locale: 'es', pageKey });
      await save(es.id, contentJson);
      try {
        const en = await api.pages.create({ title: form.title, slug: enSlug(), locale: 'en', pageKey });
        await save(en.id, contentJsonEn ?? contentJson);
      } catch { /* si falla la EN, al menos queda la ES creada */ }
      onSave(es);
      return;
    }
    const page = await api.pages.create({
      title: form.title, slug: form.slug, locale: form.locale, pageKey: form.pageKey || undefined,
    });
    await save(page.id, contentJson);
    onSave(page);
  }

  // template / blank.
  async function finishSimple() {
    if (!option) return;
    setPhase('building');
    setSaving(true);
    setError('');
    try {
      let contentJson: string | undefined;
      let contentJsonEn: string | undefined;
      if (option.mode === 'template') {
        try {
          const res = await api.ai.page({ mode: 'template', pageName: form.title, templateKey: option.templateKey, bilingual: autoBilingual });
          contentJson = res.contentJson;
          contentJsonEn = res.contentJsonEn;
        } catch { /* sin API key / límite: página vacía */ }
      }
      await createWith(contentJson, contentJsonEn);
    } catch (err) {
      setError((err as Error).message);
      setPhase('form');
      setSaving(false);
    }
  }

  // describe — paso 1: preguntas de seguimiento.
  async function goToQuestions() {
    if (!description.trim()) return;
    setSaving(true);
    setError('');
    try {
      const { questions } = await api.ai.pageQuestions(form.title, description.trim());
      if (questions && questions.length > 0) {
        setQuestions(questions);
        setAnswers({});
        setPhase('questions');
        setSaving(false);
      } else {
        await buildDescribe([]); // sin preguntas (p. ej. sin API key) → construye directo
      }
    } catch {
      await buildDescribe([]);
    }
  }

  // describe — paso 2: construir con respuestas.
  async function buildDescribe(qa: { question: string; answer: string }[]) {
    setPhase('building');
    setSaving(true);
    setError('');
    try {
      let contentJson: string | undefined;
      let contentJsonEn: string | undefined;
      try {
        const res = await api.ai.page({ mode: 'describe', pageName: form.title, description: description.trim(), answers: qa, bilingual: autoBilingual });
        contentJson = res.contentJson;
        contentJsonEn = res.contentJsonEn;
      } catch { /* sin API key / límite: página vacía */ }
      await createWith(contentJson, contentJsonEn);
    } catch (err) {
      setError((err as Error).message);
      setPhase('questions');
      setSaving(false);
    }
  }

  function buildFromAnswers() {
    const qa = questions
      .map((q, i) => (answers[i] ? { question: q.question, answer: answers[i] } : null))
      .filter((x): x is { question: string; answer: string } => x !== null);
    buildDescribe(qa);
  }

  // ── Encabezado de paso (resumen colapsado + tap para reabrir) ──
  function stepCircle(i: number, done: boolean, active: boolean) {
    const base = 'w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0';
    if (done && !active) return <span className={`${base} bg-ui-accent text-ui-bg`}><Check className="w-3.5 h-3.5" /></span>;
    if (active) return <span className={`${base} border border-ui-accent text-ui-accent`}>{i + 1}</span>;
    return <span className={`${base} border border-ui-border text-ui-text-subtle`}>{i + 1}</span>;
  }

  function StepHeader({ i, title: t, summary, done }: { i: number; title: string; summary?: string; done: boolean }) {
    const reachable = i <= maxStep;
    const active = phase === 'form' && activeStep === i;
    const clickable = reachable && phase === 'form';
    return (
      <button
        type="button"
        disabled={!clickable}
        onClick={() => clickable && setActiveStep(i)}
        className={`w-full flex items-center justify-between gap-3 py-1 ${clickable ? '' : 'cursor-default'}`}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          {stepCircle(i, done, active)}
          <div className="text-left min-w-0">
            <p className="text-sm font-medium text-ui-text">{t}</p>
            {!active && summary && <p className="text-sm text-ui-text-subtle truncate max-w-[280px]">{summary}</p>}
          </div>
        </div>
        {!active && done && clickable && <Pencil className="w-3.5 h-3.5 text-ui-text-subtle flex-shrink-0" />}
      </button>
    );
  }

  const idSummary = `${form.title || 'Sin nombre'} · ${form.slug || '—'}${form.locale === 'en' ? ' · Inglés' : ''}`;

  // ── Footer por fase / paso ──
  const cancelBtn = (
    <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-ui-text-muted hover:text-ui-text">Cancelar</button>
  );
  function primary(label: string, onClick: () => void, disabled: boolean, icon?: React.ReactNode) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="flex items-center gap-2 px-5 py-2.5 bg-ui-accent text-ui-text-inverse text-sm font-medium rounded-lg disabled:opacity-40 hover:opacity-90"
      >
        {label}{icon}
      </button>
    );
  }

  let footerBody: React.ReactNode = null;
  if (phase === 'building') {
    // El indicador de progreso vive en el body (más visible); el footer queda vacío.
    footerBody = null;
  } else if (phase === 'questions') {
    footerBody = (
      <div className="flex items-center justify-between w-full">
        <button type="button" onClick={() => setPhase('form')} className="flex items-center gap-1.5 text-sm text-ui-text-muted hover:text-ui-text">
          <ArrowLeft className="w-4 h-4" /> Atrás
        </button>
        {primary('Construir y editar', buildFromAnswers, saving, <Sparkles className="w-4 h-4" />)}
      </div>
    );
  } else if (activeStep === 0) {
    footerBody = <div className="flex items-center justify-between w-full">{cancelBtn}{primary('Continuar', confirmIdentity, !identityValid, <ArrowRight className="w-4 h-4" />)}</div>;
  } else if (activeStep === 1) {
    footerBody = <div className="flex items-center justify-start w-full">{cancelBtn}</div>;
  } else if (option?.mode === 'describe') {
    footerBody = <div className="flex items-center justify-between w-full">{cancelBtn}{primary(saving ? 'Pensando…' : 'Continuar', goToQuestions, saving || !description.trim(), <ArrowRight className="w-4 h-4" />)}</div>;
  } else if (option) {
    footerBody = <div className="flex items-center justify-between w-full">{cancelBtn}{primary(option.mode === 'blank' ? 'Crear y editar' : 'Crear con IA y editar', finishSimple, saving, <ArrowRight className="w-4 h-4" />)}</div>;
  }

  const footer = footerBody && (
    <div className="flex items-center px-4 sm:px-6 py-3 border-t border-ui-border">{footerBody}</div>
  );

  return (
    <Modal title={title} onClose={onCancel} footer={footer}>
      <div style={{ minHeight: minH || undefined }}>
      <div ref={measureRef} className="pb-1">
        {error && <p className="text-sm text-ui-danger mb-3">{error}</p>}

        {/* Resumen de pasos durante preguntas / construcción */}
        {phase !== 'form' && (
          <div className="space-y-1 mb-4 pb-3 border-b border-ui-border">
            <StepHeader i={0} title="Página" summary={idSummary} done />
            <StepHeader i={1} title="Cómo empezar" summary={option?.label} done />
          </div>
        )}

        {phase === 'questions' && (
          <div className="space-y-5">
            <p className="text-sm text-ui-text-muted">Un par de preguntas para afinar tu página. Puedes saltar las que quieras.</p>
            {questions.map((q, i) => (
              <div key={i}>
                <p className="text-sm font-medium text-ui-text mb-2">{q.question}</p>
                <div className="flex flex-wrap gap-2">
                  {q.options.map((opt) => {
                    const active = answers[i] === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setAnswers((a) => ({ ...a, [i]: active ? '' : opt }))}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? 'bg-ui-accent/15 border-ui-accent/60 text-ui-text' : 'bg-ui-bg border-ui-border text-ui-text-muted hover:border-ui-border-strong'}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {phase === 'building' && (
          <div className="flex flex-col items-center justify-center text-center py-8 gap-4">
            <span className="w-10 h-10 border-2 border-ui-accent border-t-transparent rounded-full animate-spin" />
            <div className="space-y-1.5">
              <p className="text-base font-medium text-ui-text">Armando tu página…</p>
              <p className="text-sm text-ui-text-muted max-w-sm">Estamos eligiendo las secciones y redactando los textos de tu página.</p>
            </div>
          </div>
        )}

        {phase === 'form' && (
          <div className="divide-y divide-ui-border">
            {/* Paso 0 — identidad */}
            <div className="py-2">
              <StepHeader i={0} title="Datos de la página" summary={step0Done ? idSummary : undefined} done={step0Done} />
              <AnimatePresence initial={false}>
                {activeStep === 0 && (
                  <motion.div key="s0" {...reveal} style={{ overflow: 'hidden' }}>
                    <div className="pt-3 space-y-4">
                      <Field label="Nombre de la página *" hint="Se usa para identificarla en el panel y puede mostrarse como título en el sitio.">
                        <Input value={form.title} onChange={handleTitleChange} required placeholder="Ej. Sobre nosotros" />
                      </Field>
                      <Field label="Dirección de la página *" hint="Es la parte que aparece después de tu dominio. Debe comenzar con /. Por ejemplo: /nosotros.">
                        <Input value={form.slug} onChange={(e) => set('slug', e.target.value)} required placeholder="Ej. /nosotros" />
                      </Field>
                      {autoBilingual && (
                        <p className="flex items-start gap-1.5 text-sm text-ui-text-subtle">
                          <Globe className="w-3.5 h-3.5 text-ui-accent shrink-0 mt-0.5" />
                          Tu sitio es bilingüe: crearemos esta página en español e inglés, enlazadas.
                        </p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Paso 1 — cómo empezar */}
            {maxStep >= 1 && (
              <div className="py-2">
                <StepHeader i={1} title="¿Cómo quieres empezar?" summary={step1Done ? option?.label : undefined} done={step1Done} />
                <AnimatePresence initial={false}>
                  {activeStep === 1 && (
                    <motion.div key="s1" {...reveal} style={{ overflow: 'hidden' }}>
                      <div className="pt-3 grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                        {PAGE_START_OPTIONS.map((opt) => {
                          const Icon = ICONS[opt.icon] ?? Square;
                          const selected = option?.id === opt.id;
                          return (
                            <motion.button
                              key={opt.id}
                              type="button"
                              onClick={() => chooseOption(opt)}
                              whileTap={{ scale: 0.97 }}
                              className={`relative text-left p-3 rounded-lg border transition-colors ${selected ? 'bg-ui-accent/10 border-ui-accent/50' : 'bg-ui-bg border-ui-border hover:border-ui-border-strong'}`}
                            >
                              <Icon className={`w-4 h-4 ${selected ? 'text-ui-accent' : 'text-ui-text-muted'}`} />
                              <p className="text-sm font-medium text-ui-text mt-1.5">{opt.label}</p>
                              <p className="text-sm text-ui-text-subtle mt-0.5 leading-snug">{opt.description}</p>
                            </motion.button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Paso 2 — detalle según opción */}
            {maxStep >= 2 && option && (
              <div className="py-2">
                <StepHeader i={2} title="Detalles" done={false} />
                <AnimatePresence initial={false}>
                  {activeStep === 2 && (
                    <motion.div key="s2" {...reveal} style={{ overflow: 'hidden' }}>
                      <div className="pt-3">
                        {option.mode === 'describe' ? (
                          <Field label="Cuéntanos qué quieres en esta página *" hint="La IA te hará un par de preguntas para afinarla y luego la construye.">
                            <textarea
                              value={description}
                              onChange={(e) => setDescription(e.target.value)}
                              rows={3}
                              autoFocus
                              placeholder="Ej. Una página que explique nuestro proceso de trabajo en 3 pasos y termine invitando a agendar una cita."
                              className="w-full bg-ui-bg border border-ui-border rounded-lg px-3 py-2 text-sm text-ui-text placeholder-ui-text-subtle focus:outline-none focus:border-ui-accent resize-none"
                            />
                          </Field>
                        ) : (
                          <p className="text-sm text-ui-text-subtle flex items-center gap-1.5">
                            <Sparkles className="w-3.5 h-3.5 text-ui-accent" />
                            {option.mode === 'template'
                              ? 'Prepararemos los bloques y textos por ti; podrás ajustarlo todo en el editor.'
                              : 'Empezarás con una página en blanco para armar los bloques a tu gusto.'}
                          </p>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </Modal>
  );
}
